import { useCallback, useEffect } from 'react';
import { FilesetResolver, FaceLandmarker, FaceLandmarkerOptions } from "@mediapipe/tasks-vision";
import { Vector3, Euler, Quaternion, Matrix4 } from 'three';

/**
 * Exports for your Glasses.tsx usage.
 */
let video: HTMLVideoElement;
let faceLandmarker: FaceLandmarker;

export let blendshapes: any[] = [];
export let rotation: Euler = new Euler();
export let position: Vector3 = new Vector3();
export let scale: Vector3 = new Vector3();

/**
 * IMPORTANT: We need strictly increasing timestamps to avoid
 * "Packet timestamp mismatch" errors in Mediapipe.
 */
let lastTimestamp = 0;

/**
 * Returns a strictly increasing timestamp in milliseconds.
 * If the new timestamp <= lastTimestamp, we bump it up by 1.
 */
function getSafeTimestamp(video: HTMLVideoElement): number {
  let t = Math.floor(video.currentTime * 1000); // from seconds to ms
  if (t <= lastTimestamp) {
    t = lastTimestamp + 1;
  }
  lastTimestamp = t;
  return t;
}

// Mediapipe face landmarker options
const options: FaceLandmarkerOptions = {
  baseOptions: {
    modelAssetPath:
        "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
    delegate: "GPU",
  },
  numFaces: 1,
  runningMode: "VIDEO",
  // Set outputFaceBlendshapes to true if you want the 468-dimensional face blendshape data
  outputFaceBlendshapes: false,
  outputFacialTransformationMatrixes: true,
};

export const FaceRecognition = () => {

  /**
   * Called every time the video produces a new frame (via requestVideoFrameCallback)
   */
  const onVideoFrame = useCallback(async (now: DOMHighResTimeStamp) => {
    if (!video || video.readyState < 2) {
      // If the video isn't fully ready, schedule the next callback anyway
      if ("requestVideoFrameCallback" in HTMLVideoElement.prototype) {
        video.requestVideoFrameCallback(onVideoFrame);
      } else {
        requestAnimationFrame(() => onVideoFrame(performance.now()));
      }
      return;
    }

    // 1. Get a strictly monotonic timestamp
    const safeTimestamp = getSafeTimestamp(video);

    // 2. Detect face for the current frame
    const faceLandmarkerResult = faceLandmarker.detectForVideo(
        video,
        safeTimestamp
    );

    // 3. If you need blendshapes:
    // if (faceLandmarkerResult?.faceBlendshapes?.length) {
    //   blendshapes = faceLandmarkerResult.faceBlendshapes[0].categories;
    // }

    // 4. Extract transform from the first face in the results
    if (faceLandmarkerResult?.facialTransformationMatrixes?.length) {
      const matrixData = faceLandmarkerResult.facialTransformationMatrixes[0].data;
      const matrix = new Matrix4().fromArray(matrixData);

      const positionVector = new Vector3();
      const quaternion = new Quaternion();
      const scaleVector = new Vector3();

      matrix.decompose(positionVector, quaternion, scaleVector);

      position.copy(positionVector);
      rotation.setFromQuaternion(quaternion);
      scale.copy(scaleVector);
    }

    // 5. Schedule the next frame callback
    if ("requestVideoFrameCallback" in HTMLVideoElement.prototype) {
      video.requestVideoFrameCallback(onVideoFrame);
    } else {
      requestAnimationFrame(() => onVideoFrame(performance.now()));
    }
  }, []);

  /**
   * Fallback approach if requestVideoFrameCallback is unavailable
   */
  const fallbackPredict = useCallback(() => {
    if (!video || video.readyState < 2) {
      requestAnimationFrame(fallbackPredict);
      return;
    }

    const safeTimestamp = getSafeTimestamp(video);
    const faceLandmarkerResult = faceLandmarker.detectForVideo(
        video,
        safeTimestamp
    );

    if (faceLandmarkerResult?.facialTransformationMatrixes?.length) {
      const matrixData = faceLandmarkerResult.facialTransformationMatrixes[0].data;
      const matrix = new Matrix4().fromArray(matrixData);

      const positionVector = new Vector3();
      const quaternion = new Quaternion();
      const scaleVector = new Vector3();

      matrix.decompose(positionVector, quaternion, scaleVector);
      position.copy(positionVector);
      rotation.setFromQuaternion(quaternion);
      scale.copy(scaleVector);
    }

    requestAnimationFrame(fallbackPredict);
  }, []);

  /**
   * Setup function: loads the model, gets the video stream, and starts tracking
   */
  const setup = useCallback(async () => {
    // Initialize the face landmarker
    const filesetResolver = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.3/wasm"
    );
    faceLandmarker = await FaceLandmarker.createFromOptions(
        filesetResolver,
        options
    );

    // Grab the video element
    video = document.getElementById("video") as HTMLVideoElement;

    // Request camera access
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices
          .getUserMedia({
            video: { width: 640, height: 480, frameRate: { max: 32 } },
            audio: false,
          })
          .then((stream) => {
            video.srcObject = stream;

            // Once video is loaded, begin detection
            video.addEventListener("loadeddata", () => {
              if ("requestVideoFrameCallback" in HTMLVideoElement.prototype) {
                video.requestVideoFrameCallback(onVideoFrame);
              } else {
                fallbackPredict();
              }
            });
          })
          .catch((err) => {
            console.error("Error accessing webcam:", err);
          });
    } else {
      alert("getUserMedia is not supported in this environment.");
      console.error("getUserMedia is not supported in this environment.");
    }
  }, [onVideoFrame, fallbackPredict]);

  // Start setup once on mount
  useEffect(() => {
    setup();
  }, [setup]);

  return <video id="video" autoPlay playsInline muted></video>;
};
